/**
 * Monitoring Setup
 * Initializes Sentry and LogRocket with integration
 */

import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';

/**
 * Initialize all monitoring tools
 * Call this once at app startup
 */
export function initializeMonitoring() {
  const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
  const logRocketAppId = import.meta.env.VITE_LOGROCKET_APP_ID;
  const isDevelopment = import.meta.env.DEV;
  const enableLogs = import.meta.env.VITE_ENABLE_LOGS === 'true';

  // Initialize LogRocket first (if configured)
  if (logRocketAppId) {
    LogRocket.init(logRocketAppId, {
      // Privacy settings
      console: {
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: (request) => {
          // Sanitize sensitive headers
          if (request.headers['Authorization']) {
            request.headers['Authorization'] = '[REDACTED]';
          }
          if (request.headers['authorization']) {
            request.headers['authorization'] = '[REDACTED]';
          }
          return request;
        },
        responseSanitizer: (response) => {
          // Sanitize sensitive response data
          if (response.body) {
            try {
              const body = JSON.parse(response.body);
              if (body.password) body.password = '[REDACTED]';
              if (body.token) body.token = '[REDACTED]';
              if (body.accessToken) body.accessToken = '[REDACTED]';
              if (body.refreshToken) body.refreshToken = '[REDACTED]';
              response.body = JSON.stringify(body);
            } catch {
              // Not JSON, leave as is
            }
          }
          return response;
        },
      },
      // DOM privacy settings
      dom: {
        inputSanitizer: true, // Automatically sanitize input values
        textSanitizer: true, // Sanitize text content
      },
    });

    // Set up React integration for better component tracking
    setupLogRocketReact();

    if (enableLogs) {
      console.log('✅ LogRocket initialized:', logRocketAppId);
    }
  } else if (isDevelopment && enableLogs) {
    console.warn('⚠️ LogRocket not initialized: VITE_LOGROCKET_APP_ID not found in .env.local');
  }

  // Initialize Sentry (if configured)
  if (sentryDSN) {
    Sentry.init({
      dsn: sentryDSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false, // Set to true for more privacy
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in production
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of error sessions
      
      // Integrate LogRocket with Sentry
      beforeSend: (event) => {
        // Attach LogRocket session URL to Sentry events
        if (logRocketAppId) {
          const sessionUrl = LogRocket.sessionURL;
          if (sessionUrl) {
            event.contexts = event.contexts || {};
            event.contexts.logRocket = {
              sessionURL: sessionUrl,
            };
            event.extra = event.extra || {};
            event.extra.LogRocket = sessionUrl;
          }
        }
        return event;
      },
    });

    // Send Sentry errors to LogRocket
    if (logRocketAppId) {
      LogRocket.getSessionURL((sessionURL) => {
        Sentry.setContext('LogRocket', {
          sessionURL: sessionURL,
        });
      });
    }

    if (enableLogs) {
      console.log('✅ Sentry initialized with LogRocket integration');
    }
  } else if (isDevelopment && enableLogs) {
    console.warn('⚠️ Sentry not initialized: VITE_SENTRY_DSN not found in .env.local');
  }
}

/**
 * Identify a user in monitoring tools
 * Call this after successful login
 * 
 * @param userId - Unique identifier (recommended: email for better searchability)
 * @param userTraits - Additional user information (name, email, account type, etc.)
 */
export function identifyUser(userId: string, userTraits?: {
  name?: string;
  email?: string;
  userId?: string;
  [key: string]: any;
}) {
  const logRocketAppId = import.meta.env.VITE_LOGROCKET_APP_ID;
  const enableLogs = import.meta.env.VITE_ENABLE_LOGS === 'true';

  // Identify in LogRocket (userId is the primary identifier)
  if (logRocketAppId) {
    LogRocket.identify(userId, userTraits);
  }

  // Identify in Sentry
  Sentry.setUser({
    id: userId,
    ...userTraits,
  });

  if (enableLogs) {
    console.log('✅ User identified in monitoring tools:', userId);
  }
}

/**
 * Clear user identification
 * Call this on logout
 */
export function clearUserIdentification() {
  const logRocketAppId = import.meta.env.VITE_LOGROCKET_APP_ID;
  const enableLogs = import.meta.env.VITE_ENABLE_LOGS === 'true';

  if (logRocketAppId) {
    LogRocket.identify('anonymous');
  }

  Sentry.setUser(null);

  if (enableLogs) {
    console.log('✅ User identification cleared');
  }
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, any>) {
  const logRocketAppId = import.meta.env.VITE_LOGROCKET_APP_ID;

  if (logRocketAppId) {
    LogRocket.track(eventName, properties);
  }

  // Also send to Sentry as breadcrumb
  Sentry.addBreadcrumb({
    category: 'custom-event',
    message: eventName,
    level: 'info',
    data: properties,
  });
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  // LogRocket automatically captures errors, but we can add context
  if (context) {
    LogRocket.captureException(error, {
      extra: context,
    });
  }

  // Send to Sentry
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Get LogRocket session URL
 * Useful for support tickets
 */
export function getLogRocketSessionURL(): Promise<string | null> {
  return new Promise((resolve) => {
    const logRocketAppId = import.meta.env.VITE_LOGROCKET_APP_ID;
    
    if (!logRocketAppId) {
      resolve(null);
      return;
    }

    LogRocket.getSessionURL((sessionURL) => {
      resolve(sessionURL);
    });
  });
}

